import {
    Container,
    Button,
    Box,
    Grid,
    Typography,
    IconButton,
    useTheme,
    alpha,
} from "@mui/material";
import { Share } from '@capacitor/share';
import referImage from "../../assets/refer_and_earn.png";
import CroboContainer from "../../components/croboContainer";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getProfile, redeem } from "../../reducers/userReducer";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';

const shareMessage = (referral_code, amount = 10) => {
    return `Crobo ensures instant, no-fee transfers from the US to India. Sign up with Crobo: https://crobo.money/signup/?referral_code=${referral_code}&utm_medium=link&utm_campaign=referral via my referral link and earn $${amount} instantly by using my Referral Code during signup: ${referral_code}`;
}

const Referrals = () => {
    const theme = useTheme();
    const userStore = useSelector((state) => {
        return state.userAuth?.profile
    });
    const envStore = useSelector((state) => {
        return state.resource?.envList;
    });
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getProfile());
    }, [dispatch]);
    const notify = () => toast.success("Referral code copied!");
    const notifyRedeemSuccess = () => toast.success("Redeem successfully generated, please check your email!");
    const navigate = useNavigate();
    const [isRedeemAvailable, setRedeemAvailable] = useState(userStore?.balance?.REF <= 0);
    return (
      <CroboContainer>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Grid container direction="column" spacing={2} sx={{ py: 3 }}>
            {/* Header Image */}
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <img
                src={referImage}
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  height: "auto",
                  filter:
                    theme.palette.mode === "dark" ? "brightness(0.8)" : "none",
                }}
                alt="Refer and Earn"
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Share your referral code to earn
              </Typography>
            </Grid>

            {/* Rewards Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "background.paper"
                      : "background.paper",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Your rewards
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography variant="h5" fontWeight="bold">
                    ${parseFloat(userStore?.balance?.REF || 0).toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={isRedeemAvailable}
                    onClick={() => {
                      setRedeemAvailable(true);
                      dispatch(
                        redeem({
                          data: { amount: userStore?.balance?.REF },
                          action: notifyRedeemSuccess,
                        })
                      );
                    }}
                    sx={{
                      borderRadius: 3,
                      bgcolor: "brand.main",
                      "&:hover": { bgcolor: "brand.dark" },
                      textTransform: "none",
                    }}
                  >
                    Redeem
                  </Button>
                </Box>
              </Box>
              <Button
                onClick={() => navigate("/referrals/redeem-history")}
                sx={{
                  mt: 1,
                  textDecoration: "underline",
                  textTransform: "none",
                }}
              >
                View redeem history
              </Button>
            </Grid>

            {/* Referral Code Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "background.paper"
                      : "background.paper",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Referral code
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "grey.100",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" fontFamily="monospace">
                    {userStore?.referral_code}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(userStore?.referral_code);
                      notify();
                    }}
                    sx={{ color: "brand.main" }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              </Box>
              <Button
                onClick={() => navigate("/referrals/referral-history")}
                sx={{
                  mt: 1,
                  textDecoration: "underline",
                  textTransform: "none",
                }}
              >
                View referral history
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={async () => {
                    try {
                      await Share.share({
                        title: "Crobo Referral",
                        text: shareMessage(
                          userStore?.referral_code,
                          envStore?.REFERRER_AMOUNT
                        ),
                        url: `https://crobo.money/signup/?referral_code=${userStore?.referral_code}&utm_medium=link&utm_campaign=referral`,
                        dialogTitle: "Share with friends",
                      });
                    } catch (error) {
                      console.error("Error sharing:", error);
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: "brand.main",
                    "&:hover": { bgcolor: "brand.dark" },
                    textTransform: "none",
                  }}
                  startIcon={<ShareIcon />}
                >
                  Share
                </Button>
              </Box>
            </Grid>

            {/* Info Section */}
            <Grid item xs={12}>
              <Typography variant="body1" textAlign="center" sx={{ mt: 2 }}>
                After the first transaction, both you and your referee receive a
                ${envStore?.REFERRER_AMOUNT || 25} Amazon Gift Card
              </Typography>
              <Button
                fullWidth
                onClick={() =>
                  navigate("/referrals/refer-faq", {
                    state: {
                      referral_amount: envStore?.REFERRER_AMOUNT || 25,
                      threshold_amount:
                        envStore?.THRESHOLD_REFERRAL_AMOUNT || 500,
                    },
                  })
                }
                sx={{
                  mt: 1,
                  textDecoration: "underline",
                  color: "brand.main",
                  textTransform: "none",
                }}
              >
                Know more
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={() =>
                  navigate("/condition", {
                    state: {
                      referral_amount: envStore?.REFERRER_AMOUNT || 25,
                      threshold_amount:
                        envStore?.THRESHOLD_REFERRAL_AMOUNT || 500,
                    },
                  })
                }
                sx={{
                  mt: 1,
                  textDecoration: "underline",
                  color: "brand.main",
                  textTransform: "none",
                }}
              >
                Terms and Conditions
              </Button>
            </Grid>

            {/* Share Buttons */}
          </Grid>
        </Container>
        <ToastContainer
          hideProgressBar
          position="top-center"
          theme={theme.palette.mode}
        />
      </CroboContainer>
    );
}

export default Referrals;